import * as React from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  Tab,
  Tabs,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "../../components/mui";
import { AddRoundedIcon, SearchRoundedIcon, ReplayRoundedIcon, SettingsRoundedIcon } from "../../components/icons";

import PageLayout from "../../components/shared/PageLayout";
import PageHeader from "../../components/shared/PageHeader";
import DataTableCard from "../../components/shared/DataTableCard";
import Modal from "../../components/shared/Modal";
import { runFieldValidators, requiredField } from "../../utils/validation";
import { useToast } from "../../components/shared/ToastProvider";

import "../../styles/pages/settings.css";

const INITIAL_CONDO_ROWS = [
  { id: 1, name: "Condo 1", address: "30F, Tower A, Makati", location: "Makati" },
  { id: 2, name: "Condo 2", address: "15C, Tower B, BGC", location: "Taguig" }
];

const INITIAL_MISC_ROWS = [
  { id: 1, name: "Water Refilling", fixed: true },
  { id: 2, name: "Repairs", fixed: false }
];

const INITIAL_UTILITY_ROWS = [
  { id: 1, name: "Electricity", lastUpdated: "Nov 28, 2025" },
  { id: 2, name: "Internet", lastUpdated: "Nov 12, 2025" }
];

const INITIAL_UTILITY_LOGS = [
  { id: 1, action: "Updated", actionBy: "PH296", actionOn: "19/02/2018 04:50 PM", name: "Electricity" },
  { id: 2, action: "Updated", actionBy: "PH296", actionOn: "19/02/2018 04:51 PM", name: "Electricity" }
];

const CONDO_LOCATIONS = ["Makati", "Taguig", "BGC", "Cebu", "Pasig"];

const SETTINGS_TABS = [
  {
    id: "condo",
    label: "Condo",
    tableTitle: "Condo List",
    columns: [
      { id: "name", label: "Name" },
      { id: "address", label: "Address" },
      { id: "location", label: "Location" }
    ]
  },
  {
    id: "misc",
    label: "Condo Misc Expense Type",
    tableTitle: "Misc Expense Types",
    columns: [
      { id: "name", label: "Name" },
      { id: "fixed", label: "Fixed" }
    ]
  },
  {
    id: "utility",
    label: "Condo Utility Type",
    tableTitle: "Utility Types",
    columns: [
      { id: "name", label: "Name" },
      { id: "lastUpdated", label: "Last Updated" }
    ]
  }
];

function getAddButtonLabel(tabId) {
  switch (tabId) {
    case "condo":
      return "Add Condo";
    case "misc":
      return "Add Expense Type";
    case "utility":
      return "Add Utility Type";
    default:
      return "Add";
  }
}

export default function Settings({ onLogout, onNavigate }) {
  const [activeTab, setActiveTab] = React.useState(SETTINGS_TABS[0].id);
  const [search, setSearch] = React.useState("");
  const [condoRows, setCondoRows] = React.useState(INITIAL_CONDO_ROWS);
  const [miscRows, setMiscRows] = React.useState(INITIAL_MISC_ROWS);
  const [utilityRows, setUtilityRows] = React.useState(INITIAL_UTILITY_ROWS);
  const { showToast } = useToast();

  const [isCondoModalOpen, setIsCondoModalOpen] = React.useState(false);
  const [editingCondoId, setEditingCondoId] = React.useState(null);
  const [condoFormValues, setCondoFormValues] = React.useState({ name: "", address: "", location: CONDO_LOCATIONS[0] });
  const [condoFormErrors, setCondoFormErrors] = React.useState({});

  const [isMiscModalOpen, setIsMiscModalOpen] = React.useState(false);
  const [editingMiscId, setEditingMiscId] = React.useState(null);
  const [miscFormValues, setMiscFormValues] = React.useState({ name: "", fixed: "yes" });
  const [miscFormErrors, setMiscFormErrors] = React.useState({});

  const [isUtilityModalOpen, setIsUtilityModalOpen] = React.useState(false);
  const [editingUtilityId, setEditingUtilityId] = React.useState(null);
  const [utilityFormValues, setUtilityFormValues] = React.useState({ name: "" });
  const [utilityFormErrors, setUtilityFormErrors] = React.useState({});

  const [isLogsModalOpen, setIsLogsModalOpen] = React.useState(false);
  const [selectedUtilityForLogs, setSelectedUtilityForLogs] = React.useState(null);
  const [logsData] = React.useState(INITIAL_UTILITY_LOGS);
  const [logsEntriesPerPage, setLogsEntriesPerPage] = React.useState(30);
  const [logsCurrentPage, setLogsCurrentPage] = React.useState(1);

  const activeConfig = React.useMemo(
    () => SETTINGS_TABS.find((tab) => tab.id === activeTab) || SETTINGS_TABS[0],
    [activeTab]
  );

  const rowsForTab = React.useMemo(
    () => ({
      condo: condoRows,
      misc: miscRows,
      utility: utilityRows
    }),
    [condoRows, miscRows, utilityRows]
  );

  const filteredRows = React.useMemo(() => {
    const rows = rowsForTab[activeTab] || [];
    if (!search.trim()) return rows;
    const term = search.trim().toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((value) => String(value).toLowerCase().includes(term))
    );
  }, [search, rowsForTab, activeTab]);

  const handleChangeTab = (event, value) => {
    setActiveTab(value);
    setSearch("");
  };

  const handleOpenCondoModal = (row = null) => {
    setEditingCondoId(row ? row.id : null);
    if (row) {
      setCondoFormValues({ name: row.name, address: row.address, location: row.location });
    } else {
      setCondoFormValues({ name: "", address: "", location: CONDO_LOCATIONS[0] });
    }
    setCondoFormErrors({});
    setIsCondoModalOpen(true);
  };

  const handleCloseCondoModal = () => {
    setIsCondoModalOpen(false);
    setCondoFormErrors({});
  };

  const handleCondoFormChange = (field, value) => {
    setCondoFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveCondo = () => {
    const validators = [
      { field: "name", label: "Name", validator: requiredField("{label}") },
      { field: "address", label: "Address", validator: requiredField("{label}") },
      { field: "location", label: "Location", validator: requiredField("{label}") }
    ];

    const errors = runFieldValidators(validators, condoFormValues);
    if (Object.keys(errors).length) {
      setCondoFormErrors(errors);
      return;
    }
    setCondoFormErrors({});

    if (editingCondoId) {
      setCondoRows((prev) => prev.map((row) => (row.id === editingCondoId ? { ...row, ...condoFormValues } : row)));
      showToast?.({ message: "Condo updated", severity: "success" });
    } else {
      setCondoRows((prev) => [...prev, { id: Date.now(), ...condoFormValues }]);
      showToast?.({ message: "Condo created", severity: "success" });
    }
    handleCloseCondoModal();
  };

  const handleOpenMiscModal = (row = null) => {
    setEditingMiscId(row ? row.id : null);
    if (row) {
      setMiscFormValues({ name: row.name, fixed: row.fixed ? "yes" : "no" });
    } else {
      setMiscFormValues({ name: "", fixed: "yes" });
    }
    setMiscFormErrors({});
    setIsMiscModalOpen(true);
  };

  const handleCloseMiscModal = () => {
    setIsMiscModalOpen(false);
    setMiscFormErrors({});
  };

  const handleMiscFormChange = (field, value) => {
    setMiscFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveMisc = () => {
    const validators = [{ field: "name", label: "Name", validator: requiredField("{label}") }];
    const errors = runFieldValidators(validators, miscFormValues);
    if (Object.keys(errors).length) {
      setMiscFormErrors(errors);
      return;
    }
    setMiscFormErrors({});

    const payload = { name: miscFormValues.name, fixed: miscFormValues.fixed === "yes" };

    if (editingMiscId) {
      setMiscRows((prev) => prev.map((row) => (row.id === editingMiscId ? { ...row, ...payload } : row)));
      showToast?.({ message: "Misc expense updated", severity: "success" });
    } else {
      setMiscRows((prev) => [...prev, { id: Date.now(), ...payload }]);
      showToast?.({ message: "Misc expense created", severity: "success" });
    }
    handleCloseMiscModal();
  };

  const handleOpenUtilityModal = (row = null) => {
    setEditingUtilityId(row ? row.id : null);
    if (row) {
      setUtilityFormValues({ name: row.name });
    } else {
      setUtilityFormValues({ name: "" });
    }
    setUtilityFormErrors({});
    setIsUtilityModalOpen(true);
  };

  const handleCloseUtilityModal = () => {
    setIsUtilityModalOpen(false);
    setUtilityFormErrors({});
  };

  const handleUtilityFormChange = (field, value) => {
    setUtilityFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveUtility = () => {
    const validators = [{ field: "name", label: "Name", validator: requiredField("{label}") }];
    const errors = runFieldValidators(validators, utilityFormValues);
    if (Object.keys(errors).length) {
      setUtilityFormErrors(errors);
      return;
    }
    setUtilityFormErrors({});

    if (editingUtilityId) {
      setUtilityRows((prev) => prev.map((row) => (row.id === editingUtilityId ? { ...row, name: utilityFormValues.name } : row)));
      showToast?.({ message: "Utility type updated", severity: "success" });
    } else {
      setUtilityRows((prev) => [...prev, { id: Date.now(), name: utilityFormValues.name, lastUpdated: "Just now" }]);
      showToast?.({ message: "Utility type created", severity: "success" });
    }

    handleCloseUtilityModal();
  };

  const handleOpenLogsModal = (row) => {
    setSelectedUtilityForLogs(row);
    setLogsCurrentPage(1);
    setIsLogsModalOpen(true);
  };

  const handleCloseLogsModal = () => {
    setIsLogsModalOpen(false);
    setSelectedUtilityForLogs(null);
  };

  const handleLogsEntriesChange = (value) => {
    setLogsEntriesPerPage(value);
    setLogsCurrentPage(1);
  };

  const handleLogsPageChange = (page) => {
    setLogsCurrentPage(page);
  };

  const paginatedLogs = React.useMemo(() => {
    const startIndex = (logsCurrentPage - 1) * logsEntriesPerPage;
    const endIndex = startIndex + logsEntriesPerPage;
    return logsData.slice(startIndex, endIndex);
  }, [logsData, logsCurrentPage, logsEntriesPerPage]);

  const logsTotalPages = Math.ceil(logsData.length / logsEntriesPerPage);
  const logsStartEntry = logsData.length > 0 ? (logsCurrentPage - 1) * logsEntriesPerPage + 1 : 0;
  const logsEndEntry = Math.min(logsCurrentPage * logsEntriesPerPage, logsData.length);

  const showAddButton = activeTab === "condo" || activeTab === "misc" || activeTab === "utility";
  const actionCellClass = `settings-actions-cell${activeConfig.id === "utility" ? " settings-actions-cell--utility" : ""}`;
  const actionHeaderClass = `settings-actions-header${activeConfig.id === "utility" ? " settings-actions-cell--utility" : ""}`;
  const actionStackDirection = "row";
  const actionStackSpacing = 1;
  const actionStackAlign = "center";

  return (
    <PageLayout activeKey="settings" onLogout={onLogout} onNavigate={onNavigate} className="page-content">
      <PageHeader
        icon={SettingsRoundedIcon}
        title="Settings"
        summary="Manage condo reference data such as properties, expense types, and utility types."
      />

      <Box className="tabs-container settings-tabs">
        <Tabs value={activeTab} onChange={handleChangeTab}>
          {SETTINGS_TABS.map((tab) => (
            <Tab key={tab.id} label={tab.label} value={tab.id} />
          ))}
        </Tabs>
      </Box>

      {showAddButton && (
        <Box className="settings-action-bar">
          {activeTab === "condo" && (
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => handleOpenCondoModal()}>
              {getAddButtonLabel(activeConfig.id)}
            </Button>
          )}
          {activeTab === "misc" && (
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => handleOpenMiscModal()}>
              {getAddButtonLabel(activeConfig.id)}
            </Button>
          )}
          {activeTab === "utility" && (
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => handleOpenUtilityModal()}>
              {getAddButtonLabel(activeConfig.id)}
            </Button>
          )}
        </Box>
      )}

      <Box className="content-box settings-filters">
        <Box className="settings-filters-row settings-filters-row-inline">
          <TextField
            size="small"
            label="Name"
            placeholder="Search by name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="settings-filter-name"
          />

          <Stack direction="row" spacing={1.5} className="settings-filter-actions">
            <Button variant="contained" startIcon={<SearchRoundedIcon />}>
              Search
            </Button>
            <Button variant="outlined" startIcon={<ReplayRoundedIcon />}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Box>

      <DataTableCard
        title={activeConfig.tableTitle}
        showExport={false}
        showEntriesControl
        startEntry={filteredRows.length ? 1 : 0}
        endEntry={filteredRows.length}
        totalEntries={filteredRows.length}
      >
        <TableContainer className="data-table-wrapper settings-table">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={actionHeaderClass}></TableCell>
                {activeConfig.columns.map((column) => (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeConfig.columns.length + 1} className="settings-empty-cell">
                    No records found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell className={actionCellClass}>
                      <Stack direction={actionStackDirection} spacing={actionStackSpacing} alignItems={actionStackAlign}>
                        {activeConfig.id === "condo" && (
                          <Button
                            size="small"
                            variant="text"
                            className="settings-action-button"
                            onClick={() => handleOpenCondoModal(row)}
                          >
                            Edit
                          </Button>
                        )}
                        {activeConfig.id === "misc" && (
                          <Button
                            size="small"
                            variant="text"
                            className="settings-action-button"
                            onClick={() => handleOpenMiscModal(row)}
                          >
                            Edit
                          </Button>
                        )}
                        {activeConfig.id === "utility" && (
                          <Button
                            size="small"
                            variant="text"
                            className="settings-action-button"
                            onClick={() => handleOpenUtilityModal(row)}
                          >
                            Edit
                          </Button>
                        )}
                        {activeConfig.id === "utility" && (
                          <Button 
                            size="small" 
                            variant="text" 
                            color="secondary" 
                            className="settings-action-button"
                            onClick={() => handleOpenLogsModal(row)}
                          >
                            Logs
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                    {activeConfig.columns.map((column) => {
                      const value = row[column.id];
                      if (column.id === "fixed") {
                        return <TableCell key={column.id}>{value ? "Yes" : "No"}</TableCell>;
                      }
                      return <TableCell key={column.id}>{value}</TableCell>;
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DataTableCard>

      <Modal
        open={isCondoModalOpen}
        onClose={handleCloseCondoModal}
        title={editingCondoId ? "Edit Condo" : "Add Condo"}
        customWidth="520px"
        actions={(
          <>
            <Button variant="outlined" onClick={handleCloseCondoModal}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveCondo}>
              {editingCondoId ? "Update" : "Create"}
            </Button>
          </>
        )}
      >
        <Box className="modal-form-grid">
          <Box className="modal-form-row">
            <TextField
              size="small"
              label="Name"
              value={condoFormValues.name}
              onChange={(event) => handleCondoFormChange("name", event.target.value)}
              required
              error={!!condoFormErrors.name}
              helperText={condoFormErrors.name}
              fullWidth
            />
          </Box>
          <Box className="modal-form-row">
            <TextField
              size="small"
              label="Address"
              value={condoFormValues.address}
              onChange={(event) => handleCondoFormChange("address", event.target.value)}
              required
              error={!!condoFormErrors.address}
              helperText={condoFormErrors.address}
              fullWidth
            />
          </Box>
          <Box className="modal-form-row">
            <FormControl size="small" fullWidth required error={!!condoFormErrors.location}>
              <InputLabel>Location</InputLabel>
              <Select
                label="Location"
                value={condoFormValues.location}
                onChange={(event) => handleCondoFormChange("location", event.target.value)}
              >
                {CONDO_LOCATIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {condoFormErrors.location && (
                <Typography variant="caption" color="error">
                  {condoFormErrors.location}
                </Typography>
              )}
            </FormControl>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={isMiscModalOpen}
        onClose={handleCloseMiscModal}
        title={editingMiscId ? "Edit Misc Expense Type" : "Add Misc Expense Type"}
        customWidth="420px"
        actions={(
          <>
            <Button variant="outlined" onClick={handleCloseMiscModal}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveMisc}>
              {editingMiscId ? "Update" : "Create"}
            </Button>
          </>
        )}
      >
        <Box className="modal-form-grid">
          <Box className="modal-form-row">
            <TextField
              size="small"
              label="Name"
              value={miscFormValues.name}
              onChange={(event) => handleMiscFormChange("name", event.target.value)}
              required
              error={!!miscFormErrors.name}
              helperText={miscFormErrors.name}
              fullWidth
            />
          </Box>
          <Box className="modal-form-row">
            <FormControl size="small" fullWidth>
              <InputLabel>Fixed</InputLabel>
              <Select
                label="Fixed"
                value={miscFormValues.fixed}
                onChange={(event) => handleMiscFormChange("fixed", event.target.value)}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={isUtilityModalOpen}
        onClose={handleCloseUtilityModal}
        title={editingUtilityId ? "Edit Utility Type" : "Add Utility Type"}
        customWidth="360px"
        actions={(
          <>
            <Button variant="outlined" onClick={handleCloseUtilityModal}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveUtility}>
              {editingUtilityId ? "Update" : "Create"}
            </Button>
          </>
        )}
      >
        <Box className="modal-form-grid">
          <Box className="modal-form-row">
            <TextField
              size="small"
              label="Name"
              value={utilityFormValues.name}
              onChange={(event) => handleUtilityFormChange("name", event.target.value)}
              required
              error={!!utilityFormErrors.name}
              helperText={utilityFormErrors.name}
              fullWidth
            />
          </Box>
        </Box>
      </Modal>

      <Modal
        open={isLogsModalOpen}
        onClose={handleCloseLogsModal}
        title="Condo Utility Type Logs"
        customWidth="800px"
        actions={(
          <Button variant="contained" onClick={handleCloseLogsModal}>
            Close
          </Button>
        )}
      >
        <Box className="modal-form-grid">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography component="span" variant="body2" className="modal-show-entries-text">
              Show
            </Typography>
            <FormControl size="small" className="data-table-select">
              <Select 
                value={logsEntriesPerPage}
                onChange={(event) => handleLogsEntriesChange(event.target.value)}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
            <Typography component="span" variant="body2" className="modal-show-entries-text">
              entries
            </Typography>
          </Box>

          <TableContainer className="data-table-wrapper">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className="modal-table-header">Action</TableCell>
                  <TableCell className="modal-table-header">Action By</TableCell>
                  <TableCell className="modal-table-header">Action On</TableCell>
                  <TableCell className="modal-table-header">Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No logs found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedLogs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>{log.action}</TableCell>
                      <TableCell>{log.actionBy}</TableCell>
                      <TableCell>{log.actionOn}</TableCell>
                      <TableCell>{log.name}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className="modal-pagination-info">
            <Typography variant="caption" className="modal-pagination-text">
              Showing {logsStartEntry} to {logsEndEntry} of {logsData.length} entries
            </Typography>
            <Box display="flex" gap={0.5}>
              <Button 
                variant="outlined" 
                size="small" 
                className="table-pagination-button" 
                onClick={() => handleLogsPageChange(logsCurrentPage - 1)}
                disabled={logsCurrentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                size="small"
                className="table-pagination-button is-active"
              >
                {logsCurrentPage}
              </Button>
              <Button 
                variant="outlined" 
                size="small" 
                className="table-pagination-button" 
                onClick={() => handleLogsPageChange(logsCurrentPage + 1)}
                disabled={logsCurrentPage === logsTotalPages || logsTotalPages === 0}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </PageLayout>
  );
}
